import type { CSSProperties } from 'react';
import type { AnalysisType } from '../../shared/types';

/* ── Identité colorée des types d'analyse ────────────────────────────
   Chaque type porte une couleur du nuancier : elle habille sa tuile sur
   l'accueil, puis, une fois l'analyse ouverte, elle habille les ÉLÉMENTS
   de l'écran — en-tête, champs, puces, cartes — pendant que le fond reste
   le crème de la charte. L'utilisateur reconnaît donc « où il est » à la
   couleur seule, sans qu'un aplat plein écran écrase la lecture.

   Source unique de vérité — utilisée par AnalysisTypeSelector (tuiles +
   animation d'ouverture) et par App (variables CSS de la zone de contenu). */

export type Tint = {
  /** Classe d'aplat définie dans index.css (@layer components). */
  className: string;
  /** Aplat signature en hexadécimal, pour les fonds animés en style inline. */
  hex: string;
  /** Déclinaisons exposées en variables CSS sur la zone de contenu. */
  scale: {
    /** Aplat plein — en-têtes, puces sélectionnées. */
    base: string;
    /** Fond des champs de saisie — assez soutenu pour se voir, assez clair
     *  pour porter du texte encre. */
    field: string;
    /** Fond de carte douce — blocs d'aide, cartouches. */
    soft: string;
    /** Ton soutenu — filets, accents, `accent-color`. */
    strong: string;
    /** Ton profond — texte coloré lisible sur `soft`. */
    deep: string;
  };
};

function tint(className: string, base: string, field: string, soft: string, strong: string, deep: string): Tint {
  return { className, hex: base, scale: { base, field, soft, strong, deep } };
}

export const TINTS: Record<string, Tint> = {
  sun:     tint('tint-sun',     '#f5dd6d', '#f9e79b', '#fdf8e4', '#e8c230', '#8d7118'),
  rose:    tint('tint-rose',    '#f5bcdb', '#f8cfe6', '#fdf1f8', '#e77fb9', '#93406e'),
  sky:     tint('tint-sky',     '#b7cdf0', '#cfdef5', '#f2f6fd', '#6f92d3', '#3a5280'),
  khaki:   tint('tint-khaki',   '#c4d3a4', '#d5e0b9', '#f4f7ec', '#8fa95c', '#566635'),
  clay:    tint('tint-clay',    '#efac80', '#f5c8a9', '#fdf3ec', '#d97a3c', '#8a4922'),
  neutral: tint('tint-neutral', '#f2ece1', '#f4eee4', '#fbf7f0', '#a8a093', '#413d36'),
  ink:     tint('tint-ink',     '#101010', '#24221f', '#413d36', '#5d584f', '#e7e0d3'),
};

/* Une teinte par typologie. Cinq familles pour neuf types : les doublons
   sont volontairement éloignés dans la grille pour rester distinguables. */
const BY_ANALYSIS: Record<string, keyof typeof TINTS> = {
  general_question: 'sky',
  brand_name: 'sun',
  slogan: 'rose',
  concept: 'khaki',
  label: 'clay',
  campaign: 'sun',
  social_post: 'rose',
  event_activation: 'sky',
  creative_brief: 'khaki',
};

/** Teinte d'un type d'analyse. Retombe sur le neutre crème si inconnu. */
export function analysisTint(key: AnalysisType | string | null | undefined): Tint {
  if (!key) return TINTS.neutral;
  return TINTS[BY_ANALYSIS[key] ?? 'neutral'];
}

/** Variables CSS à poser sur un conteneur : tout ce qu'il contient peut
 *  alors se colorer via `var(--tint-*)` sans connaître le type d'analyse. */
export function tintVars(key: AnalysisType | string | null | undefined): CSSProperties {
  const { scale } = analysisTint(key);
  return {
    '--tint': scale.base,
    '--tint-field': scale.field,
    '--tint-soft': scale.soft,
    '--tint-strong': scale.strong,
    '--tint-deep': scale.deep,
  } as CSSProperties;
}

/** Surface applicative par défaut — le crème du cadre. */
export const APP_SURFACE = '#fbf6ee';
